// PocketBase-backed repository: shared dataset with auth and per-record
// ownership. The 99-column row is stored as a JSON blob (`data`); see
// pocketbase/pb_migrations for the collection schema and access rules.

import PocketBase from "pocketbase";
import type { Row } from "../util";
import type { Repository, SessionUser } from "./types";
import { recordToRow, rowToPayload, type RecordModel } from "./mapping";

const COLLECTION = "records";

export class PocketBaseRepository implements Repository {
  private pb: PocketBase;

  constructor(url: string) {
    this.pb = new PocketBase(url);
    // Refresh the persisted auth token (if any) in the background.
    if (this.pb.authStore.isValid) {
      this.pb.collection("users").authRefresh().catch(() => this.pb.authStore.clear());
    }
  }

  currentUser(): SessionUser | null {
    // Guard against an expired token whose record is still cached in
    // authStore — otherwise the bootstrap gate (!repo.currentUser()) would
    // let the form render, and the next repo.list() would fail with 401.
    if (!this.pb.authStore.isValid) return null;
    const rec = this.pb.authStore.record;
    if (!rec) return null;
    return { id: rec.id, email: (rec.email as string) ?? "", role: (rec.role as string) ?? "doctor" };
  }

  async login(email: string, password: string): Promise<void> {
    await this.pb.collection("users").authWithPassword(email, password);
  }

  logout(): void {
    this.pb.authStore.clear();
  }

  private ownerId(): string {
    return this.pb.authStore.record?.id ?? "";
  }

  async list(): Promise<Row[]> {
    const records = await this.pb.collection(COLLECTION).getFullList({ sort: "created" });
    return records.map((rec) => recordToRow(rec as unknown as RecordModel));
  }

  async upsert(row: Row): Promise<Row> {
    const payload = rowToPayload(row, this.ownerId());
    const saved = row._pb_id
      ? await this.pb.collection(COLLECTION).update(row._pb_id, payload)
      : await this.pb.collection(COLLECTION).create(payload);
    return recordToRow(saved as unknown as RecordModel);
  }

  async bulkUpsert(rows: Row[]): Promise<Row[]> {
    const out: Row[] = [];
    for (const row of rows) out.push(await this.upsert(row));
    return out;
  }

  async remove(row: Row): Promise<void> {
    if (row._pb_id) await this.pb.collection(COLLECTION).delete(row._pb_id);
  }

  async removeMany(rows: Row[]): Promise<void> {
    for (const row of rows) await this.remove(row);
  }
}
